import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/PortalShell";
import { useAuth } from "@/lib/auth";
import { fetchCampaigns, saveCampaign, setCampaignStatus } from "@/lib/marketing";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">
      {label}
    </span>
    {children}
  </label>
);

const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm";

/** Marketing → Campaign Settings. AWARE-side definition only — audiences,
    budgets, bidding, placements and creative stay in Meta Ads Manager. */
export default function CampaignSettings() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "marketing";
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => fetchCampaigns().then((d) => setRows(d.campaigns))
    .catch(() => toast.error("Unable to load campaigns"));
  useEffect(() => { load(); }, []);

  const copy = (text, what) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${what} copied`);
  };

  const save = async (c) => {
    setSaving(true);
    try {
      await saveCampaign(c.campaign_id, draft[c.campaign_id] || {});
      toast.success("Campaign settings saved");
      setDraft((d) => ({ ...d, [c.campaign_id]: {} }));
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PortalShell title="Campaign Settings"
      subtitle="Marketing · the AWARE-side definition of each campaign">
      <div className="flex flex-wrap gap-2 mb-5">
        <Link to="/admin/marketing/campaign-performance" data-testid="settings-link-performance"
          className="rounded-full border border-slate-300 px-4 py-2 text-xs font-bold">
          View Performance
        </Link>
        <Link to="/admin/marketing/ad-spend" data-testid="settings-link-ad-spend"
          className="rounded-full border border-slate-300 px-4 py-2 text-xs font-bold">
          View Ad Spend
        </Link>
      </div>
      {!canEdit && (
        <p data-testid="settings-read-only"
          className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs
            font-bold text-amber-800 mb-4">
          Read-only access — campaign settings can only be changed by an administrator.
        </p>
      )}
      {!rows && (
        <p className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </p>
      )}

      <div className="space-y-4">
        {(rows || []).map((c) => {
          const d = { ...c, ...(draft[c.campaign_id] || {}) };
          const patch = (key, value) => setDraft((prev) => ({
            ...prev, [c.campaign_id]: { ...(prev[c.campaign_id] || {}), [key]: value } }));
          return (
            <section key={c.campaign_id} data-testid={`settings-card-${c.campaign_id}`}
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-black text-slate-900">{c.campaign_name}</h2>
                  <p className="text-xs text-slate-500">
                    {c.campaign_id} · {c.campaign_source} · {c.platform}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Link data-testid={`settings-view-performance-${c.campaign_id}`}
                    to={`/admin/marketing/campaign-performance/${c.campaign_id}`}
                    className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-bold">
                    View Performance
                  </Link>
                  <button data-testid={`settings-copy-url-${c.campaign_id}`}
                    onClick={() => copy(`${window.location.origin}${c.landing_page}`,
                      "Campaign URL")}
                    className="inline-flex items-center gap-1 rounded-full border
                      border-slate-300 px-3 py-1.5 text-xs font-bold">
                    <Copy className="h-3 w-3" /> Copy Campaign URL
                  </button>
                  <button data-testid={`settings-copy-utm-${c.campaign_id}`}
                    onClick={() => copy(c.utm_template, "UTM template")}
                    className="inline-flex items-center gap-1 rounded-full border
                      border-slate-300 px-3 py-1.5 text-xs font-bold">
                    <Copy className="h-3 w-3" /> Copy UTM Template
                  </button>
                  {canEdit && (
                    <button data-testid={`settings-toggle-${c.campaign_id}`}
                      onClick={() => setCampaignStatus(c.campaign_id, !c.active)
                        .then(() => { toast.success("Campaign status updated"); load(); })}
                      className="rounded-full bg-slate-900 px-3 py-1.5 text-xs font-bold text-white">
                      {c.active ? "Deactivate" : "Activate"}
                    </button>
                  )}
                </div>
              </div>

              <div data-testid={`settings-health-${c.campaign_id}`}
                className="flex flex-wrap gap-2 mt-3 text-[11px] font-bold">
                {[[c.active ? "ACTIVE" : "INACTIVE", c.active],
                  [`Landing tracking: ${c.landing_visit_tracking ? "ON" : "OFF"}`,
                   c.landing_visit_tracking],
                  [`Start tracking: ${c.health_check_start_tracking ? "ON" : "OFF"}`,
                   c.health_check_start_tracking],
                  ["Attribution: First touch", true],
                  [`Spend source: ${c.spend_source === "manual" ? "Manual" : "Meta API"}`, true],
                  [`Last activity: ${c.last_activity ? c.last_activity.slice(0, 10) : "—"}`,
                   !!c.last_activity],
                ].map(([label, ok]) => (
                  <span key={label} className={`rounded-full px-2 py-0.5 border ${
                    ok ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                    {label}
                  </span>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                <Field label="Campaign name">
                  <input data-testid="settings-campaign-name" className={input}
                    disabled={!canEdit} value={d.campaign_name || ""}
                    onChange={(e) => patch("campaign_name", e.target.value)} />
                </Field>
                <Field label="Landing page">
                  <input data-testid="settings-landing-page" className={input}
                    disabled={!canEdit} value={d.landing_page || ""}
                    onChange={(e) => patch("landing_page", e.target.value)} />
                </Field>
                <Field label="Reporting campaign id (utm_campaign)">
                  <input data-testid="settings-reporting-campaign-id" className={input}
                    disabled={!canEdit} value={d.reporting_campaign_id || ""}
                    placeholder="blank = report on every campaign tag"
                    onChange={(e) => patch("reporting_campaign_id", e.target.value)} />
                </Field>
                <Field label="Start date">
                  <input type="date" data-testid="settings-start-date" className={input}
                    disabled={!canEdit} value={d.start_date || ""}
                    onChange={(e) => patch("start_date", e.target.value)} />
                </Field>
                <Field label="End date">
                  <input type="date" data-testid="settings-end-date" className={input}
                    disabled={!canEdit} value={d.end_date || ""}
                    onChange={(e) => patch("end_date", e.target.value)} />
                </Field>
                <Field label="Reporting currency">
                  <input data-testid="settings-currency" className={input} disabled={!canEdit}
                    value={d.reporting_currency || ""}
                    onChange={(e) => patch("reporting_currency", e.target.value)} />
                </Field>
                <Field label="UTM naming rules">
                  <input data-testid="settings-utm-rules" className={input} disabled={!canEdit}
                    value={d.utm_naming_rules || ""}
                    onChange={(e) => patch("utm_naming_rules", e.target.value)} />
                </Field>
                <Field label="Notes">
                  <input data-testid="settings-notes" className={input} disabled={!canEdit}
                    value={d.notes || ""} onChange={(e) => patch("notes", e.target.value)} />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  {[["landing_visit_tracking", "Landing visits"],
                    ["health_check_start_tracking", "Health Check starts"],
                    ["meta_schedule_tracking", "Meta Schedule"],
                    ["meta_purchase_tracking", "Meta Purchase"]].map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 text-xs font-bold">
                      <input type="checkbox" data-testid={`settings-${key}`}
                        disabled={!canEdit} checked={!!d[key]}
                        onChange={(e) => patch(key, e.target.checked)} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-xs text-slate-600">
                <p>Primary attribution: <b>first touch</b></p>
                <p>Secondary: <b>last touch / assists</b></p>
                <p>PWS window: <b>{c.pws_attribution_window_days} days</b></p>
                <p>Lab offer: <b>{c.lab_offer_enabled
                  ? `${c.lab_discount_percent}% off` : "off"}</b></p>
                <p className="sm:col-span-4">Eligible lab packages:{" "}
                  <b>{(c.eligible_lab_packages || []).join(", ")}</b></p>
                <p className="sm:col-span-4 text-slate-500">
                  Audiences, budgets, bidding, placements and ad creative are managed in
                  Meta Ads Manager — never here.
                </p>
              </div>

              {canEdit && (
                <button data-testid={`settings-save-${c.campaign_id}`} disabled={saving}
                  onClick={() => save(c)}
                  className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4
                    py-2 text-xs font-bold text-white mt-4 disabled:opacity-50">
                  <Save className="h-3 w-3" /> Save settings
                </button>
              )}
            </section>
          );
        })}
      </div>
    </PortalShell>
  );
}
